import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { VenueResearchService, type VenueResearch } from '@/services/VenueResearchService';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  MapPin,
  Building2,
  Users,
  Star,
  Clock,
  DollarSign,
  FileJson,
  AlertCircle,
  CheckCircle2,
  Link as LinkIcon,
  Copy,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationStore } from '@/stores/notificationStore';

interface ResearchDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  researchId: string;
}

/**
 * Format duration in seconds
 */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '-';
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes} minute${minutes > 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
}

export function ResearchDetailsModal({ isOpen, onClose, researchId }: ResearchDetailsModalProps) {
  const [showRawJSON, setShowRawJSON] = useState(false);
  const { success } = useNotificationStore();

  // Fetch research details
  const { data: research, isLoading, error } = useQuery({
    queryKey: ['venue-research-details', researchId],
    queryFn: () => VenueResearchService.getResearchById(researchId),
    enabled: isOpen && !!researchId,
  });

  // Fetch discovered venues
  const { data: venues } = useQuery({
    queryKey: ['venue-research-venues', researchId],
    queryFn: () => VenueResearchService.getResearchVenues(researchId),
    enabled: isOpen && !!researchId && research?.status === 'completed',
  });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    success(`${label} copied to clipboard`);
  };

  if (isLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Research Details" size="xl">
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </Modal>
    );
  }

  if (error || !research) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Research Details" size="xl">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-error-600 mx-auto mb-4" />
          <p className="text-error-600">Failed to load research details</p>
          <p className="text-sm text-gray-500 mt-2">{(error as Error)?.message || 'Unknown error'}</p>
        </div>
      </Modal>
    );
  }

  const statusVariant =
    research.status === 'completed'
      ? 'success'
      : research.status === 'failed'
      ? 'error'
      : research.status === 'processing'
      ? 'warning'
      : 'secondary';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Research Details" size="xl">
      <div className="space-y-6">
        {/* Header with Status */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <MapPin className="w-6 h-6 text-primary-600" />
              <h3 className="text-lg font-semibold text-gray-900">{research.search_city}</h3>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatDistanceToNow(new Date(research.created_at), { addSuffix: true })}
              </span>
              {research.llm_total_cost_usd && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  ${parseFloat(research.llm_total_cost_usd).toFixed(4)}
                </span>
              )}
            </div>
          </div>
          <Badge variant={statusVariant} className="text-base px-4 py-1">
            {research.status.charAt(0).toUpperCase() + research.status.slice(1)}
          </Badge>
        </div>

        {/* Search Criteria */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900 flex items-center gap-2">
            <FileJson className="w-4 h-4" />
            Search Criteria
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {research.min_rooms && (
              <div className="flex items-center gap-2 text-gray-700">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span>Min Rooms: {research.min_rooms}</span>
              </div>
            )}
            {research.min_conference_capacity && (
              <div className="flex items-center gap-2 text-gray-700">
                <Users className="w-4 h-4 text-gray-400" />
                <span>Min Capacity: {research.min_conference_capacity}</span>
              </div>
            )}
            {research.min_stars && (
              <div className="flex items-center gap-2 text-gray-700">
                <Star className="w-4 h-4 text-gray-400" />
                <span>Min Stars: {research.min_stars}</span>
              </div>
            )}
          </div>
          {!research.min_rooms && !research.min_conference_capacity && !research.min_stars && (
            <p className="text-sm text-gray-500">No filter criteria specified</p>
          )}
        </div>

        {/* Results Summary */}
        {research.status === 'completed' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary-700 font-medium">Venues Found</p>
                  <p className="text-2xl font-bold text-primary-900 mt-1">{research.venues_found}</p>
                </div>
                <MapPin className="w-8 h-8 text-primary-600" />
              </div>
            </div>
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-success-700 font-medium">Created</p>
                  <p className="text-2xl font-bold text-success-900 mt-1">{research.venues_created}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-success-600" />
              </div>
            </div>
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warning-700 font-medium">Updated</p>
                  <p className="text-2xl font-bold text-warning-900 mt-1">{research.venues_updated}</p>
                </div>
                <Building2 className="w-8 h-8 text-warning-600" />
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {research.status === 'failed' && research.error_message && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-error-900">Error</h4>
                <p className="text-sm text-error-700 mt-1">{research.error_message}</p>
                {research.error_code && (
                  <p className="text-xs text-error-600 mt-2">Code: {research.error_code}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Agent Notes */}
        {research.agent_notes && research.agent_notes.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">AI Agent Notes</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {research.agent_notes.map((note, index) => (
                <div key={index} className="flex items-start gap-2 text-sm">
                  <span className="text-gray-400 font-mono">•</span>
                  <span className="text-gray-700">{note}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Discovered Venues */}
        {venues && venues.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Discovered Venues ({venues.length})</h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <div className="space-y-2">
                {venues.map((venue: any) => (
                  <div
                    key={venue.id}
                    className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{venue.property_name}</p>
                      <p className="text-xs text-gray-500">{venue.city}</p>
                    </div>
                    <a
                      href={`/organization/venues?search=${encodeURIComponent(venue.property_name)}`}
                      className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
                      onClick={onClose}
                    >
                      <LinkIcon className="w-3 h-3" />
                      View
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Execution Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Duration</p>
            <p className="font-medium text-gray-900">{formatDuration(research.duration_seconds)}</p>
          </div>
          <div>
            <p className="text-gray-500">Model</p>
            <p className="font-medium text-gray-900">{research.llm_model || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Started</p>
            <p className="font-medium text-gray-900">
              {research.started_at ? new Date(research.started_at).toLocaleString() : '-'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Completed</p>
            <p className="font-medium text-gray-900">
              {research.completed_at ? new Date(research.completed_at).toLocaleString() : '-'}
            </p>
          </div>
        </div>

        {/* LLM Response (Raw JSON) */}
        {research.llm_response_json && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-900">LLM Response</h4>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowRawJSON(!showRawJSON)}
                >
                  {showRawJSON ? 'Hide' : 'Show'} JSON
                </Button>
                {showRawJSON && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(research.llm_response_json, null, 2),
                        'JSON response'
                      )
                    }
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                )}
              </div>
            </div>
            {showRawJSON && (
              <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-x-auto max-h-96">
                {JSON.stringify(research.llm_response_json, null, 2)}
              </pre>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
